import {
    Directive,
    inject,
    Input,
    OnInit,
    TemplateRef,
    ViewContainerRef
} from '@angular/core';
import { englishLanguageLocale } from '../constants-types';
import { InitDataService } from '../system-services';

/**
 * Directive to conditionally display content based on the user's language.
 * If the user's language matches the specified `language` input, the template will be displayed.
 * Otherwise, if `languageElse` is provided, that template will be displayed.
 *
 * @example
 * ```html
 * <!-- Show content only for English users -->
 * <div *language="'en-US'">
 *   This content is only visible to English users.
 * </div>
 *
 * <!-- Show different content based on language -->
 * <div *language="'en-US'; else spanishTemplate">
 *   Welcome! (English)
 * </div>
 * <ng-template #spanishTemplate>
 *   ¡Bienvenido! (Spanish)
 * </ng-template>
 *
 * <!-- Using with language constants -->
 * <div *language="englishLanguageLocale; else otherLanguageTemplate">
 *   English-specific content
 * </div>
 * <ng-template #otherLanguageTemplate>
 *   Content for other languages
 * </ng-template>
 * ```
 */
@Directive({
    selector: '[language]',
    standalone: true
})
export class LanguageDirective implements OnInit {
    /**
     * The language to check against the current user's language.
     * If the current user's language matches this value, the template will be displayed.
     * Otherwise, if `languageElse` is provided, that template will be displayed.
     */
    @Input()
    set language(value: string) {
        this.#allowedLanguage = value;
        this.displayTemplate();
    }
    /**
     * Can be specified to include an else clause so that a template
     * can be used in case the language does not match the current user's language.
     */
    @Input()
    languageElse?: TemplateRef<unknown>;

    #allowedLanguage: string = englishLanguageLocale;

    readonly #initData = inject(InitDataService);
    readonly #viewContainer = inject(ViewContainerRef);
    readonly #templateRef = inject(TemplateRef<any>);

    #userLanguage: string = englishLanguageLocale;

    ngOnInit(): void {
        this.#userLanguage = this.#initData.companySettings.languageLocale;
        this.displayTemplate();
    }

    /**
     * Manages display state of the template
     */
    private displayTemplate() {
        this.#viewContainer.clear();
        if (this.#userLanguage === this.#allowedLanguage) {
            this.#viewContainer.createEmbeddedView(this.#templateRef);
        } else if (this.languageElse) {
            this.#viewContainer.createEmbeddedView(this.languageElse);
        }
    }
}
